import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { Controller, Path, useForm } from "react-hook-form";
import {
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import NurseDateField from "../NurseDateField";
import { normalizeDateInputValue } from "../dateValueUtils";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

export interface Step10FormValues {
    // Lung/Breathing
    lungNoProblem: boolean; lungBreathingActivity: boolean; lungBreathingResting: boolean;
    lungWheezing: boolean; lungCoughing: boolean; lungCOPD: boolean;
    lungSinus: boolean; lungOxygen: boolean; lungOther: string; lungComments: string;
    // Cardiovascular
    cardioNoProblem: boolean; cardioIrregularHB: boolean; cardioHeartDisease: boolean;
    cardioPacemaker: boolean; cardioCHF: boolean; cardioHighBP: boolean; cardioLowBP: boolean;
    cardioStroke: boolean; cardioTIA: boolean; cardioDizziness: boolean;
    cardioAnticoagulation: boolean; cardioColdFeet: boolean; cardioNumbness: boolean;
    cardioVaricoseVeins: boolean; cardioEdema: string; cardioOther: string; cardioComments: string;
    // GI
    giNoProblem: boolean; giHeartburnGERD: boolean; giRegurgitates: boolean;
    giAbdominalPain: boolean; giNausea: boolean; giHemorrhoids: boolean;
    giBlackBloody: boolean; giColostomy: boolean; giTubeFeedings: boolean; giComments: string;
    // Kidney/Urinary
    kidneyNoProblem: boolean; kidneyUTICurrent: boolean; kidneyUTIFrequent: boolean;
    kidneyUTIChronic: boolean; kidneyStones: boolean; kidneyEnlargedProstate: boolean;
    kidneyIndwellingCatheter: boolean; kidneyOtherDisease: boolean; kidneyComments: string;
    // Endocrine
    endocrineNoProblem: boolean; endocrineThyroidHypo: boolean; endocrineThyroidHyper: boolean;
    endocrineDiabetesIDDM: boolean; endocrineDiabetesNIDDM: boolean;
    endocrineInsulin: boolean; endocrineMeds: boolean; endocrineDiet: boolean;
    endocrineSelfBSTest: boolean; endocrineSelfInsulin: boolean; endocrineComments: string;
    // Bladder
    bladderLevel: "noProblem" | "usuallyContinent" | "occasionallyIncontinent" | "frequentlyIncontinent" | "totallyIncontinent" | "";
    bladderUrinaryRetention: boolean; bladderFoleyCatheter: boolean;
    bladderIntermittentCath: boolean; bladderComments: string;
    // Bowel
    bowelLevel: "noProblem" | "occasionallyIncontinent" | "frequentlyIncontinent" | "totallyIncontinent" | "";
    bowelSuppository: boolean; bowelComments: string;
    // Musculoskeletal
    muscoNoProblem: boolean; muscoLimitedROM: boolean; muscoMissingLimb: string;
    muscoContractures: boolean; muscoFootProblems: boolean; muscoBoneJointPain: boolean;
    muscoOrthoDevice: string; muscoWeakness: boolean; muscoFracture: boolean;
    muscoFallRisk: boolean; muscoComments: string;
    // Skin
    skinNoProblem: boolean; skinDry: boolean; skinFragile: boolean;
    skinIncision: string; skinBruise: string; skinTears: boolean;
    skinMoles: string; skinItchy: boolean; skinComments: string;
    // Nervous System
    neuroNoProblem: boolean; neuroTremors: boolean; neuroSeizures: boolean;
    neuroSpasticity: boolean; neuroComments: string;
    // Communicable Disease
    commDiseaseNoProblem: boolean; commDiseaseTB: boolean; commDiseaseHepatitis: boolean;
    commDiseaseViral: string; commDiseaseComments: string;
    // Immunizations
    immunTBDate: string; immunFluDate: string; immunHepatitisDate: string;
    immunPneumoniaDate: string; immunComments: string;
    // Pain Management
    painHasPain: boolean; painSeverity: string; painLocation: string;
    painDuration: string; painCause: string; painComments: string;
    // Substance Use
    substanceAlcohol: boolean; substanceAlcoholHistory: string;
    substanceTobacco: boolean; substanceDrugAddiction: string; substanceComments: string;
}

export const DEFAULT_STEP10: Step10FormValues = {
    lungNoProblem: false, lungBreathingActivity: false, lungBreathingResting: false,
    lungWheezing: false, lungCoughing: false, lungCOPD: false,
    lungSinus: false, lungOxygen: false, lungOther: "", lungComments: "",
    cardioNoProblem: false, cardioIrregularHB: false, cardioHeartDisease: false,
    cardioPacemaker: false, cardioCHF: false, cardioHighBP: false, cardioLowBP: false,
    cardioStroke: false, cardioTIA: false, cardioDizziness: false,
    cardioAnticoagulation: false, cardioColdFeet: false, cardioNumbness: false,
    cardioVaricoseVeins: false, cardioEdema: "", cardioOther: "", cardioComments: "",
    giNoProblem: false, giHeartburnGERD: false, giRegurgitates: false,
    giAbdominalPain: false, giNausea: false, giHemorrhoids: false,
    giBlackBloody: false, giColostomy: false, giTubeFeedings: false, giComments: "",
    kidneyNoProblem: false, kidneyUTICurrent: false, kidneyUTIFrequent: false,
    kidneyUTIChronic: false, kidneyStones: false, kidneyEnlargedProstate: false,
    kidneyIndwellingCatheter: false, kidneyOtherDisease: false, kidneyComments: "",
    endocrineNoProblem: false, endocrineThyroidHypo: false, endocrineThyroidHyper: false,
    endocrineDiabetesIDDM: false, endocrineDiabetesNIDDM: false,
    endocrineInsulin: false, endocrineMeds: false, endocrineDiet: false,
    endocrineSelfBSTest: false, endocrineSelfInsulin: false, endocrineComments: "",
    bladderLevel: "", bladderUrinaryRetention: false, bladderFoleyCatheter: false,
    bladderIntermittentCath: false, bladderComments: "",
    bowelLevel: "", bowelSuppository: false, bowelComments: "",
    muscoNoProblem: false, muscoLimitedROM: false, muscoMissingLimb: "",
    muscoContractures: false, muscoFootProblems: false, muscoBoneJointPain: false,
    muscoOrthoDevice: "", muscoWeakness: false, muscoFracture: false,
    muscoFallRisk: false, muscoComments: "",
    skinNoProblem: false, skinDry: false, skinFragile: false,
    skinIncision: "", skinBruise: "", skinTears: false,
    skinMoles: "", skinItchy: false, skinComments: "",
    neuroNoProblem: false, neuroTremors: false, neuroSeizures: false,
    neuroSpasticity: false, neuroComments: "",
    commDiseaseNoProblem: false, commDiseaseTB: false, commDiseaseHepatitis: false,
    commDiseaseViral: "", commDiseaseComments: "",
    immunTBDate: "", immunFluDate: "", immunHepatitisDate: "", immunPneumoniaDate: "", immunComments: "",
    painHasPain: false, painSeverity: "", painLocation: "", painDuration: "", painCause: "", painComments: "",
    substanceAlcohol: false, substanceAlcoholHistory: "",
    substanceTobacco: false, substanceDrugAddiction: "", substanceComments: "",
};

export interface Step10Props {
    sectionData: Partial<Step10FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step10FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

const formatSelected = (items: Array<string | false | null | undefined>) => {
    const selected = items.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    return selected.length ? selected.join(", ") : undefined;
};

const BLADDER_LEVEL_LABELS: Record<Step10FormValues["bladderLevel"], string> = {
    "": "",
    noProblem: "No problem identified",
    usuallyContinent: "Usually continent",
    occasionallyIncontinent: "Occasionally incontinent",
    frequentlyIncontinent: "Frequently incontinent",
    totallyIncontinent: "Totally incontinent",
};

const BOWEL_LEVEL_LABELS: Record<Step10FormValues["bowelLevel"], string> = {
    "": "",
    noProblem: "No problem identified",
    occasionallyIncontinent: "Occasionally incontinent",
    frequentlyIncontinent: "Frequently incontinent",
    totallyIncontinent: "Totally incontinent",
};

const Step10MedicalSystemsReview: React.FC<Step10Props> = ({
    sectionData, formId, onSave, onNext, onBack, loading,
}) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);
    const defaultValues: Step10FormValues = {
        ...DEFAULT_STEP10,
        ...(sectionData ?? {}),
        immunTBDate: normalizeDateInputValue(sectionData?.immunTBDate),
        immunFluDate: normalizeDateInputValue(sectionData?.immunFluDate),
        immunHepatitisDate: normalizeDateInputValue(sectionData?.immunHepatitisDate),
        immunPneumoniaDate: normalizeDateInputValue(sectionData?.immunPneumoniaDate),
    };
    const { control, handleSubmit, watch, reset } = useForm<Step10FormValues>({ defaultValues });

    const onSubmit = async (data: Step10FormValues) => { await onSave(data); };
    const handleSave = async (data: Step10FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Medical systems review saved.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    const handleSaveAndContinue = async (data: Step10FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    useNurseFormAutoSave({ enabled: enableEditing && !!formId, baselineData: defaultValues, watch, handleSubmit, onSubmit });

    const values = watch();

    const cb = (name: Path<Step10FormValues>, label: string) => (
        <Controller key={name} name={name} control={control} render={({ field: f }) => (
            <div className="flex align-items-center gap-2 mb-1">
                <Checkbox inputId={name} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                <label htmlFor={name} className="text-sm cursor-pointer">{label}</label>
            </div>
        )} />
    );

    const txt = (name: Path<Step10FormValues>, label: string, placeholder = "") => (
        <Controller key={name} name={name} control={control} render={({ field: f }) => (
            <div className="mt-1">
                <label className="font-medium text-sm block mb-1">{label}</label>
                <InputText {...(f as any)} className="w-full" placeholder={placeholder || label} />
            </div>
        )} />
    );

    const comments = (name: Path<Step10FormValues>) => (
        <Controller key={name} name={name} control={control} render={({ field: f }) => (
            <div className="mt-2">
                <label className="font-medium text-sm block mb-1">Comments</label>
                <InputTextarea {...(f as any)} className="w-full" rows={2} autoResize placeholder="Comments" />
            </div>
        )} />
    );

    const radioLevel = <K extends string>(fieldName: Path<Step10FormValues>, options: Array<{ value: K; label: string }>) => (
        <Controller name={fieldName} control={control} render={({ field: f }) => (
            <div className="flex flex-column gap-1 mb-2">
                {options.map(opt => (
                    <div key={opt.value as string} className="flex align-items-start gap-2">
                        <RadioButton inputId={`${fieldName}_${opt.value}`} value={opt.value} checked={f.value === opt.value} onChange={() => f.onChange(opt.value)} style={{ marginTop: 2 }} />
                        <label htmlFor={`${fieldName}_${opt.value}`} className="text-sm cursor-pointer">{opt.label}</label>
                    </div>
                ))}
            </div>
        )} />
    );

    const immunDate = (name: Path<Step10FormValues>, label: string) => (
        <div key={name} className="col-12 md:col-6">
            <label className="font-medium text-sm block mb-1">{label} — Date</label>
            <Controller name={name} control={control} render={({ field: f }) => (
                <NurseDateField value={f.value as string} onChange={f.onChange} placeholder={`${label} date`} className="w-full" />
            )} />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Medical Systems Review", "Body systems assessment summary.", "pi pi-heart")}
                            <div className="p-3 grid">
                                <InfoRow label="Lung / Breathing" value={values.lungNoProblem ? "No Problems" : formatSelected([
                                    values.lungBreathingActivity && "Shortness of breath during activity",
                                    values.lungBreathingResting && "Shortness of breath at rest",
                                    values.lungWheezing && "Wheezing",
                                    values.lungCoughing && "Coughing",
                                    values.lungCOPD && "COPD",
                                    values.lungSinus && "Sinus problems",
                                    values.lungOxygen && "Oxygen",
                                    values.lungOther && `Other: ${values.lungOther}`,
                                ])} />
                                <InfoRow label="Lung Comments" value={values.lungComments} />
                                <InfoRow label="Cardiovascular" value={values.cardioNoProblem ? "No Problems" : formatSelected([
                                    values.cardioIrregularHB && "Irregular heartbeat/palpitations",
                                    values.cardioHeartDisease && "Heart disease",
                                    values.cardioPacemaker && "Pacemaker",
                                    values.cardioCHF && "CHF",
                                    values.cardioHighBP && "High blood pressure",
                                    values.cardioLowBP && "Low blood pressure",
                                    values.cardioStroke && "Stroke",
                                    values.cardioTIA && "TIA",
                                    values.cardioDizziness && "Dizziness",
                                    values.cardioAnticoagulation && "Anticoagulation therapy",
                                    values.cardioColdFeet && "Cold feet",
                                    values.cardioNumbness && "Numbness",
                                    values.cardioVaricoseVeins && "Varicose veins",
                                    values.cardioEdema && `Edema: ${values.cardioEdema}`,
                                    values.cardioOther && `Other: ${values.cardioOther}`,
                                ])} />
                                <InfoRow label="Cardio Comments" value={values.cardioComments} />
                                <InfoRow label="GI" value={values.giNoProblem ? "No Problems" : formatSelected([
                                    values.giHeartburnGERD && "Heartburn/GERD",
                                    values.giRegurgitates && "Regurgitates food",
                                    values.giAbdominalPain && "Abdominal pain",
                                    values.giNausea && "Nausea/vomiting",
                                    values.giHemorrhoids && "Hemorrhoids",
                                    values.giBlackBloody && "Black or bloody stools",
                                    values.giColostomy && "Colostomy",
                                    values.giTubeFeedings && "Tube feedings",
                                ])} />
                                <InfoRow label="GI Comments" value={values.giComments} />
                                <InfoRow label="Kidney / Urinary" value={values.kidneyNoProblem ? "No Problems" : formatSelected([
                                    values.kidneyUTICurrent && "Current UTI",
                                    values.kidneyUTIFrequent && "Frequent UTI",
                                    values.kidneyUTIChronic && "Chronic UTI",
                                    values.kidneyStones && "Kidney stones",
                                    values.kidneyEnlargedProstate && "Enlarged prostate",
                                    values.kidneyIndwellingCatheter && "Indwelling catheter",
                                    values.kidneyOtherDisease && "Other kidney disease",
                                ])} />
                                <InfoRow label="Kidney Comments" value={values.kidneyComments} />
                                <InfoRow label="Endocrine" value={values.endocrineNoProblem ? "No Problems" : formatSelected([
                                    values.endocrineThyroidHypo && "Thyroid hypo",
                                    values.endocrineThyroidHyper && "Thyroid hyper",
                                    values.endocrineDiabetesIDDM && "Diabetes IDDM",
                                    values.endocrineDiabetesNIDDM && "Diabetes NIDDM",
                                    values.endocrineInsulin && "Controlled with insulin",
                                    values.endocrineMeds && "Controlled with meds",
                                    values.endocrineDiet && "Controlled with diet",
                                    values.endocrineSelfBSTest && "Self blood sugar test",
                                    values.endocrineSelfInsulin && "Self insulin admin",
                                ])} />
                                <InfoRow label="Endocrine Comments" value={values.endocrineComments} />
                                <InfoRow label="Bladder" value={formatSelected([
                                    BLADDER_LEVEL_LABELS[values.bladderLevel],
                                    values.bladderUrinaryRetention && "Urinary retention",
                                    values.bladderFoleyCatheter && "Foley catheter",
                                    values.bladderIntermittentCath && "Intermittent catheterization",
                                ])} />
                                <InfoRow label="Bladder Comments" value={values.bladderComments} />
                                <InfoRow label="Bowel" value={formatSelected([
                                    BOWEL_LEVEL_LABELS[values.bowelLevel],
                                    values.bowelSuppository && "Needs suppository or digital stimulation",
                                ])} />
                                <InfoRow label="Bowel Comments" value={values.bowelComments} />
                                <InfoRow label="Musculoskeletal" value={values.muscoNoProblem ? "No Problems" : formatSelected([
                                    values.muscoLimitedROM && "Limited range of motion",
                                    values.muscoContractures && "Contractures",
                                    values.muscoFootProblems && "Foot problems",
                                    values.muscoBoneJointPain && "Bone/joint pain",
                                    values.muscoWeakness && "Weakness",
                                    values.muscoFracture && "Fracture",
                                    values.muscoFallRisk && "Fall risk",
                                    values.muscoMissingLimb && `Missing limb(s): ${values.muscoMissingLimb}`,
                                    values.muscoOrthoDevice && `Orthopedic device(s): ${values.muscoOrthoDevice}`,
                                ])} />
                                <InfoRow label="Musculoskeletal Comments" value={values.muscoComments} />
                                <InfoRow label="Skin" value={values.skinNoProblem ? "No Problems" : formatSelected([
                                    values.skinDry && "Dry skin",
                                    values.skinFragile && "Fragile skin",
                                    values.skinTears && "Tears",
                                    values.skinItchy && "Itchy",
                                    values.skinIncision && `Incision(s): ${values.skinIncision}`,
                                    values.skinBruise && `Bruise(s): ${values.skinBruise}`,
                                    values.skinMoles && `Moles/growths: ${values.skinMoles}`,
                                ])} />
                                <InfoRow label="Skin Comments" value={values.skinComments} />
                                <InfoRow label="Nervous System" value={values.neuroNoProblem ? "No Problems" : formatSelected([
                                    values.neuroTremors && "Tremors",
                                    values.neuroSeizures && "Seizures",
                                    values.neuroSpasticity && "Spasticity",
                                ])} />
                                <InfoRow label="Neuro Comments" value={values.neuroComments} />
                                <InfoRow label="Communicable Disease" value={values.commDiseaseNoProblem ? "No Problems" : formatSelected([
                                    values.commDiseaseTB && "Tuberculosis",
                                    values.commDiseaseHepatitis && "Hepatitis",
                                    values.commDiseaseViral && `Viral infection(s): ${values.commDiseaseViral}`,
                                ])} />
                                <InfoRow label="Communicable Disease Comments" value={values.commDiseaseComments} />
                                <InfoRow label="Immunizations" value={formatSelected([
                                    values.immunTBDate && `Tuberculosis test: ${values.immunTBDate}`,
                                    values.immunFluDate && `Flu: ${values.immunFluDate}`,
                                    values.immunHepatitisDate && `Hepatitis: ${values.immunHepatitisDate}`,
                                    values.immunPneumoniaDate && `Pneumonia: ${values.immunPneumoniaDate}`,
                                ])} />
                                <InfoRow label="Immunization Comments" value={values.immunComments} />
                                <InfoRow label="Pain" value={values.painHasPain ? formatSelected([
                                    "Has pain",
                                    values.painSeverity && `Severity: ${values.painSeverity}`,
                                    values.painLocation && `Location: ${values.painLocation}`,
                                    values.painDuration && `Duration: ${values.painDuration}`,
                                    values.painCause && `Cause: ${values.painCause}`,
                                ]) : "No"} />
                                <InfoRow label="Pain Comments" value={values.painComments} />
                                <InfoRow label="Substance Use" value={formatSelected([
                                    values.substanceAlcohol && "Alcohol use",
                                    values.substanceAlcoholHistory && `Alcohol history: ${values.substanceAlcoholHistory}`,
                                    values.substanceTobacco && "Tobacco use",
                                    values.substanceDrugAddiction && `Drug addiction history: ${values.substanceDrugAddiction}`,
                                ])} />
                                <InfoRow label="Substance Comments" value={values.substanceComments} />
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        {/* Lung/Breathing */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Lung/Breathing Problems", "", "pi pi-circle")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("lungNoProblem", "No problem identified")}</div>
                                    <div className="col-6 md:col-4">{cb("lungBreathingActivity", "Shortness of breath — During Activity")}</div>
                                    <div className="col-6 md:col-4">{cb("lungBreathingResting", "Shortness of breath — When Resting")}</div>
                                    <div className="col-6 md:col-4">{cb("lungWheezing", "Wheezing")}</div>
                                    <div className="col-6 md:col-4">{cb("lungCoughing", "Coughing")}</div>
                                    <div className="col-6 md:col-4">{cb("lungCOPD", "COPD")}</div>
                                    <div className="col-6 md:col-4">{cb("lungSinus", "Sinus problems")}</div>
                                    <div className="col-6 md:col-4">{cb("lungOxygen", "Oxygen")}</div>
                                    <div className="col-12">{txt("lungOther", "Other")}</div>
                                </div>
                                {comments("lungComments")}
                            </div>
                        </Card>

                        {/* Cardiovascular */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Cardiovascular Problems", "", "pi pi-heart")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("cardioNoProblem", "No problem identified")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioIrregularHB", "Irregular HB/palpitations")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioHeartDisease", "Heart disease")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioPacemaker", "Pacemaker")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioCHF", "CHF")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioHighBP", "High blood pressure")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioLowBP", "Low blood pressure")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioStroke", "Stroke")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioTIA", "TIA")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioDizziness", "Dizziness")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioAnticoagulation", "Anticoagulation therapy")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioColdFeet", "Cold feet")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioNumbness", "Numbness")}</div>
                                    <div className="col-6 md:col-4">{cb("cardioVaricoseVeins", "Varicose veins")}</div>
                                    <div className="col-12 md:col-6">{txt("cardioEdema", "Edema (describe)")}</div>
                                    <div className="col-12 md:col-6">{txt("cardioOther", "Other")}</div>
                                </div>
                                {comments("cardioComments")}
                            </div>
                        </Card>

                        {/* GI */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Gastro-intestinal Problems", "", "pi pi-circle-fill")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("giNoProblem", "No problem identified")}</div>
                                    <div className="col-6 md:col-4">{cb("giHeartburnGERD", "Heartburn/GERD")}</div>
                                    <div className="col-6 md:col-4">{cb("giRegurgitates", "Regurgitates food")}</div>
                                    <div className="col-6 md:col-4">{cb("giAbdominalPain", "Abdominal pain")}</div>
                                    <div className="col-6 md:col-4">{cb("giNausea", "Nausea/vomiting")}</div>
                                    <div className="col-6 md:col-4">{cb("giHemorrhoids", "Hemorrhoids")}</div>
                                    <div className="col-6 md:col-4">{cb("giBlackBloody", "Black or bloody stools")}</div>
                                    <div className="col-6 md:col-4">{cb("giColostomy", "Colostomy")}</div>
                                    <div className="col-6 md:col-4">{cb("giTubeFeedings", "Tube feedings")}</div>
                                </div>
                                {comments("giComments")}
                            </div>
                        </Card>

                        {/* Kidney/Urinary */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Kidney/Urinary Tract Problems", "", "pi pi-circle")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("kidneyNoProblem", "No problem identified")}</div>
                                    <div className="col-6 md:col-4">{cb("kidneyUTICurrent", "UTI — Current")}</div>
                                    <div className="col-6 md:col-4">{cb("kidneyUTIFrequent", "UTI — Frequent")}</div>
                                    <div className="col-6 md:col-4">{cb("kidneyUTIChronic", "UTI — Chronic")}</div>
                                    <div className="col-6 md:col-4">{cb("kidneyStones", "Kidney stones")}</div>
                                    <div className="col-6 md:col-4">{cb("kidneyEnlargedProstate", "Enlarged prostate")}</div>
                                    <div className="col-6 md:col-4">{cb("kidneyIndwellingCatheter", "Indwelling catheter")}</div>
                                    <div className="col-6 md:col-4">{cb("kidneyOtherDisease", "Other kidney disease")}</div>
                                </div>
                                {comments("kidneyComments")}
                            </div>
                        </Card>

                        {/* Endocrine */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Endocrine Problems", "", "pi pi-circle")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("endocrineNoProblem", "No problem identified")}</div>
                                    <div className="col-6 md:col-4">{cb("endocrineThyroidHypo", "Thyroid — Hypo")}</div>
                                    <div className="col-6 md:col-4">{cb("endocrineThyroidHyper", "Thyroid — Hyper")}</div>
                                    <div className="col-6 md:col-4">{cb("endocrineDiabetesIDDM", "Diabetes — IDDM")}</div>
                                    <div className="col-6 md:col-4">{cb("endocrineDiabetesNIDDM", "Diabetes — NIDDM")}</div>
                                    <div className="col-6 md:col-4">{cb("endocrineInsulin", "Blood sugar controlled: Insulin")}</div>
                                    <div className="col-6 md:col-4">{cb("endocrineMeds", "Blood sugar controlled: Meds")}</div>
                                    <div className="col-6 md:col-4">{cb("endocrineDiet", "Blood sugar controlled: Diet")}</div>
                                    <div className="col-6 md:col-4">{cb("endocrineSelfBSTest", "Self blood sugar test")}</div>
                                    <div className="col-6 md:col-4">{cb("endocrineSelfInsulin", "Self insulin admin")}</div>
                                </div>
                                {comments("endocrineComments")}
                            </div>
                        </Card>

                        {/* Bladder */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Bladder Problems", "", "pi pi-circle")}
                            <div className="p-3">
                                {radioLevel("bladderLevel", [
                                    { value: "noProblem", label: "No problem identified" },
                                    { value: "usuallyContinent", label: "Usually continent (incontinent no more than once per week)" },
                                    { value: "occasionallyIncontinent", label: "Occasionally incontinent (two or more times per week / urgency)" },
                                    { value: "frequentlyIncontinent", label: "Frequently incontinent (daily)" },
                                    { value: "totallyIncontinent", label: "Totally incontinent" },
                                ])}
                                {cb("bladderUrinaryRetention", "Urinary retention")}
                                {cb("bladderFoleyCatheter", "Foley catheter")}
                                {cb("bladderIntermittentCath", "In-out intermittent catheterization")}
                                {comments("bladderComments")}
                            </div>
                        </Card>

                        {/* Bowel */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Bowel Problems", "", "pi pi-circle")}
                            <div className="p-3">
                                {radioLevel("bowelLevel", [
                                    { value: "noProblem", label: "No problem identified" },
                                    { value: "occasionallyIncontinent", label: "Occasionally incontinent (no more than once per week)" },
                                    { value: "frequentlyIncontinent", label: "Frequently incontinent (two-three times per week)" },
                                    { value: "totallyIncontinent", label: "Totally incontinent" },
                                ])}
                                {cb("bowelSuppository", "Needs suppository or digital stimulation")}
                                {comments("bowelComments")}
                            </div>
                        </Card>

                        {/* Musculoskeletal */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Musculoskeletal Problems", "", "pi pi-circle")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("muscoNoProblem", "No problem identified")}</div>
                                    <div className="col-6 md:col-4">{cb("muscoLimitedROM", "Limited range of motion")}</div>
                                    <div className="col-6 md:col-4">{cb("muscoContractures", "Contractures")}</div>
                                    <div className="col-6 md:col-4">{cb("muscoFootProblems", "Foot problems")}</div>
                                    <div className="col-6 md:col-4">{cb("muscoBoneJointPain", "Bone/joint pain")}</div>
                                    <div className="col-6 md:col-4">{cb("muscoWeakness", "Weakness")}</div>
                                    <div className="col-6 md:col-4">{cb("muscoFracture", "Fracture")}</div>
                                    <div className="col-6 md:col-4">{cb("muscoFallRisk", "Fall risk")}</div>
                                    <div className="col-12 md:col-6">{txt("muscoMissingLimb", "Missing limb(s)")}</div>
                                    <div className="col-12 md:col-6">{txt("muscoOrthoDevice", "Orthopedic device(s)")}</div>
                                </div>
                                {comments("muscoComments")}
                            </div>
                        </Card>

                        {/* Skin */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Skin Problems", "", "pi pi-circle")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("skinNoProblem", "No problem identified")}</div>
                                    <div className="col-6 md:col-4">{cb("skinDry", "Dry skin")}</div>
                                    <div className="col-6 md:col-4">{cb("skinFragile", "Fragile skin")}</div>
                                    <div className="col-6 md:col-4">{cb("skinTears", "Tears")}</div>
                                    <div className="col-6 md:col-4">{cb("skinItchy", "Itchy")}</div>
                                    <div className="col-12 md:col-4">{txt("skinIncision", "Incision(s)")}</div>
                                    <div className="col-12 md:col-4">{txt("skinBruise", "Bruise(s)")}</div>
                                    <div className="col-12 md:col-4">{txt("skinMoles", "Moles/growths")}</div>
                                </div>
                                {comments("skinComments")}
                            </div>
                        </Card>

                        {/* Nervous System */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Nervous System Problems", "", "pi pi-bolt")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-3">{cb("neuroNoProblem", "No problem identified")}</div>
                                    <div className="col-6 md:col-3">{cb("neuroTremors", "Tremors")}</div>
                                    <div className="col-6 md:col-3">{cb("neuroSeizures", "Seizures")}</div>
                                    <div className="col-6 md:col-3">{cb("neuroSpasticity", "Spasticity")}</div>
                                </div>
                                {comments("neuroComments")}
                            </div>
                        </Card>

                        {/* Communicable Disease */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Communicable Disease History", "", "pi pi-shield")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-3">{cb("commDiseaseNoProblem", "No problem identified")}</div>
                                    <div className="col-6 md:col-3">{cb("commDiseaseTB", "Tuberculosis")}</div>
                                    <div className="col-6 md:col-3">{cb("commDiseaseHepatitis", "Hepatitis")}</div>
                                    <div className="col-12">{txt("commDiseaseViral", "Viral infection(s)")}</div>
                                </div>
                                {comments("commDiseaseComments")}
                            </div>
                        </Card>

                        {/* Immunizations */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Immunizations", "Record dates of immunizations.", "pi pi-calendar-plus")}
                            <div className="p-3">
                                <div className="grid">
                                    {immunDate("immunTBDate", "Tuberculosis Test")}
                                    {immunDate("immunFluDate", "Flu")}
                                    {immunDate("immunHepatitisDate", "Hepatitis")}
                                    {immunDate("immunPneumoniaDate", "Pneumonia")}
                                </div>
                                {comments("immunComments")}
                            </div>
                        </Card>

                        {/* Pain Management */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Pain Management", "Current pain status and management.", "pi pi-exclamation-circle")}
                            <div className="p-3">
                                {cb("painHasPain", "Has pain")}
                                <div className="grid mt-2">
                                    <div className="col-12 md:col-3">{txt("painSeverity", "Severity (1-10)")}</div>
                                    <div className="col-12 md:col-3">{txt("painLocation", "Location")}</div>
                                    <div className="col-12 md:col-3">{txt("painDuration", "Duration")}</div>
                                    <div className="col-12 md:col-3">{txt("painCause", "Cause")}</div>
                                </div>
                                {comments("painComments")}
                            </div>
                        </Card>

                        {/* Substance Use */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Substance Use", "Alcohol, tobacco, and drug use history.", "pi pi-info-circle")}
                            <div className="p-3">
                                {cb("substanceAlcohol", "Drinks alcohol")}
                                <div className="mt-2 mb-2">{txt("substanceAlcoholHistory", "History of alcohol problems/treatment")}</div>
                                {cb("substanceTobacco", "Tobacco use")}
                                <div className="mt-2 mb-2">{txt("substanceDrugAddiction", "Current or past drug addiction")}</div>
                                {comments("substanceComments")}
                            </div>
                        </Card>

                    </>
                )}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step10MedicalSystemsReview;
